import { BehaviorSubject } from "rxjs";
import { LineLayerSpecification, Map as MapboxMap } from "@iventis/mapbox-gl";
import { LineString, Feature } from "geojson";
import { MapObjectProperties } from "@iventis/map-types";
import { createStaticStyleValue } from "@iventis/layer-style-helpers/src/create-style-value-helpers";
import { LineStyle } from "@iventis/domain-model/model/lineStyle";
import { LayerStorageScope, MapModuleLayer, MapState } from "../../types/store-schema";
import { Layer } from "../generic-layers/layer";
import { setRemoteSourceLayer, styleValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import { StylePropertyToValueMapV2 } from "../../types/style-types";
import { IMapboxLayerManager, MapboxLayerManager } from "./mapbox-layer-manager";
import { LayerSourceType } from "../../types/internal";
import { MapboxHighlightLayer } from "./mapbox-highlight-layer";
import { MapboxHighlightLineLayer } from "./mapbox-highlight-line-layer";
import { IventisMapboxLayerMetaDataUserLayer } from "../mapbox/test-helpers/mapbox-test-helper-types";

type LineSource = Feature<LineString, MapObjectProperties>[];

type SupportedLineMapboxLayers = LineLayerSpecification;

/** Base class for any line-based layer */
export class MapboxLineBaseLayer extends Layer<LineSource, Partial<LineStyle>> {
    protected readonly layerId: string;

    protected readonly sourceId: string;

    private readonly mapboxLayerManager: IMapboxLayerManager<LineSource>;

    private readonly highlightsLayer: MapboxHighlightLayer;

    public get hasLocalLayer() {
        return this.mapboxLayerManager.hasLocalLayer;
    }

    public get hasRemoteLayer() {
        return this.mapboxLayerManager.hasRemoteLayer;
    }

    constructor(
        map: MapboxMap,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        private layerMetaData: IventisMapboxLayerMetaDataUserLayer,
        private readonly accessors: {
            getAboveLayerId: () => string;
            getStyle: (layer: MapModuleLayer) => Partial<LineStyle>;
        }
    ) {
        super(state$);
        this.layerId = layer.id;
        this.sourceId = layer.id;
        this.mapboxLayerManager = new MapboxLayerManager(map, this.layerId);
        this.highlightsLayer = new MapboxHighlightLineLayer(this.layerId, map);
    }

    public addLayer(layerSourceType: LayerSourceType) {
        if (layerSourceType === "local") {
            this.mapboxLayerManager.createLocalLayerClass();
            this.mapboxLayerManager.addLocalSource();
        }
        if (layerSourceType === "remote") {
            this.mapboxLayerManager.createRemoteLayerClass();
        }
        const layer = this.getIventisLayer();
        const mapboxLayer = this.iventisLayerToMapboxLayer(layer);
        this.mapboxLayerManager.addLayerToMapbox(mapboxLayer, { layerSourceType, aboveLayerId: this.accessors.getAboveLayerId() });
        this.updateMapLevel(this.state$.value.currentLevel);
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.mapboxLayerManager.addDateFilter(layer.dataFields, dateFilter.day, dateFilter.time);
        }
    }

    public removeLayer() {
        this.mapboxLayerManager.removeLayer();
    }

    public addLocalSource() {
        if (!this.mapboxLayerManager.hasLocalLayer) {
            this.addLayer("local");
        }
        this.mapboxLayerManager.addLocalSource();
    }

    public updateLocalSource(source: LineSource): void {
        if (!this.mapboxLayerManager.hasLocalLayer) {
            this.addLayer("local");
        }
        this.mapboxLayerManager.updateLocalSource(source);
    }

    public removeLocalSource(): void {
        this.mapboxLayerManager.removeLocalSource();
    }

    public hideLayer(): void {
        this.mapboxLayerManager.hideLayer();
    }

    public showLayer(): void {
        this.mapboxLayerManager.showLayer();
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.mapboxLayerManager.updateMapOrder(aboveLayerId);
    }

    public updateMapLevel(level: number) {
        this.mapboxLayerManager.updateMapLevel(level);
    }

    public addDateFilter() {
        const layer = this.getIventisLayer();
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.mapboxLayerManager.addDateFilter(layer.dataFields, dateFilter.day, dateFilter.time);
        }
    }

    public removeDateFilter() {
        this.mapboxLayerManager.removeDateFilter();
    }

    public async updateStyle(styleChanges: StylePropertyToValueMapV2<LineStyle>) {
        if (styleChanges.colour) {
            this.mapboxLayerManager.setPaintProperty("line-color", styleValueToMapboxStyleValue(styleChanges.colour));
        }
    }

    protected iventisLayerToMapboxLayer(iventisLayer: MapModuleLayer): SupportedLineMapboxLayers {
        const style = this.accessors.getStyle(iventisLayer);
        const layer: LineLayerSpecification = {
            id: iventisLayer.id,
            type: "line",
            paint: {
                "line-color": styleValueToMapboxStyleValue(style.colour),
                "line-width": styleValueToMapboxStyleValue(style.width),
            },
            layout: {
                visibility: iventisLayer.visible ? "visible" : "none",
                "line-sort-key": styleValueToMapboxStyleValue(style.objectOrder ?? createStaticStyleValue(0)),
            },
            source: iventisLayer.source,
            metadata: this.layerMetaData,
        };
        if (iventisLayer.storageScope === LayerStorageScope.LocalAndTiles) {
            setRemoteSourceLayer(layer, iventisLayer.id);
        }

        return layer;
    }

    public highlightObjects(objectIds: string[]): void {
        const geojson = this.getGeoJson();
        if (geojson == null) {
            return;
        }
        const features = geojson.filter((feature) => objectIds.includes(feature.properties?.id));
        this.highlightsLayer.upsertSource(features);
        this.highlightsLayer.addLayer(this.getIventisLayer(), this.mapboxLayerManager.localLayerId);
    }

    public removeHighlights(): void {
        this.highlightsLayer.removeLayer();
        this.highlightsLayer.removeSource();
    }

    public getLayerIdsForSnapping(): string[] {
        return this.mapboxLayerManager.getActiveLayerIds();
    }
}
